import { z } from "zod";
import { ALLOCATION_PERCENT_STEP } from "@common/constants/domains/accounting/schedules";
import { accountingLedgerBasisSchema } from "@common/schemas/domains/accounting/ledger.schema";
import {
    ExpenseSubMode,
    RecognitionAnchor,
    RecognitionMethod,
    RevenueSubMode,
    ScheduleApplicationPeriodRole,
    ScheduleApplicationPeriodStatus,
    ScheduleApplicationStatus,
    ScheduleJeSubtype,
    ScheduleLifecycleVerb,
    ScheduleMilestoneAnchor,
    ScheduleMilestoneOffsetUnit,
    ScheduleSourceType,
    ScheduleTemplateStatus,
    ScheduleTermUnit,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

export const scheduleTypeSchema = z.nativeEnum(ScheduleType);

export const revenueSubModeSchema = z.nativeEnum(RevenueSubMode);

export const expenseSubModeSchema = z.nativeEnum(ExpenseSubMode);

export const scheduleTemplateStatusSchema = z.nativeEnum(
    ScheduleTemplateStatus,
);

export const scheduleApplicationStatusSchema = z.nativeEnum(
    ScheduleApplicationStatus,
);

export const recognitionMethodSchema = z.nativeEnum(RecognitionMethod);

export const recognitionAnchorSchema = z.nativeEnum(RecognitionAnchor);

export const scheduleTermUnitSchema = z.nativeEnum(ScheduleTermUnit);

export const scheduleJeSubtypeSchema = z.nativeEnum(ScheduleJeSubtype);

export const scheduleApplicationPeriodStatusSchema = z.nativeEnum(
    ScheduleApplicationPeriodStatus,
);

export const scheduleSourceTypeSchema = z.nativeEnum(ScheduleSourceType);

export const scheduleLifecycleVerbSchema = z.nativeEnum(ScheduleLifecycleVerb);

export const scheduleTemplateBaseSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    type: scheduleTypeSchema,
    revenueSubMode: revenueSubModeSchema.nullable(),
    expenseSubMode: expenseSubModeSchema.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    group: z.string(),
    termLength: z.number().int(),
    termUnit: scheduleTermUnitSchema,
    salvageAmount: z.number().nullable(),
    status: scheduleTemplateStatusSchema,
    createdByUserId: z.number().nullable(),
    createdAt: z.coerce.date(),
    updatedByUserId: z.number().nullable(),
    updatedAt: z.coerce.date(),
});

export type ScheduleTemplateBase = z.infer<typeof scheduleTemplateBaseSchema>;

export const scheduleRecognitionComponentBaseSchema = z.object({
    id: z.string().uuid(),
    scheduleTemplateId: z.string().uuid(),
    order: z.number().int(),
    method: recognitionMethodSchema,
    anchor: recognitionAnchorSchema,
    offsetDays: z.number().int(),
    allocationPercent: z.number(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type ScheduleRecognitionComponentBase = z.infer<
    typeof scheduleRecognitionComponentBaseSchema
>;

export const scheduleTemplateAccountBaseSchema = z.object({
    id: z.string().uuid(),
    scheduleTemplateId: z.string().uuid(),
    entityId: z.number().int(),
    basis: accountingLedgerBasisSchema,
    holdingAccountId: z.string().uuid(),
    recognitionAccountId: z.string().uuid(),
    contraAssetAccountId: z.string().uuid().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type ScheduleTemplateAccountBase = z.infer<
    typeof scheduleTemplateAccountBaseSchema
>;

export enum ScheduleSourcePreviousCodingKind {
    transactionOverride = "transaction_override",
    invoiceLineOverrides = "invoice_line_overrides",
    billLineOverrides = "bill_line_overrides",
}

export const scheduleSourcePreviousCodingSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal(ScheduleSourcePreviousCodingKind.transactionOverride),
        previousAccountId: z.string().uuid().nullable(),
    }),
    z.object({
        kind: z.literal(ScheduleSourcePreviousCodingKind.invoiceLineOverrides),
        previousOverrides: z.array(
            z.object({
                invoiceLineItemId: z.string().uuid(),
                accountId: z.string().uuid().nullable(),
            }),
        ),
    }),
    z.object({
        kind: z.literal(ScheduleSourcePreviousCodingKind.billLineOverrides),
        previousOverrides: z.array(
            z.object({
                billLineItemId: z.string().uuid(),
                accountId: z.string().uuid().nullable(),
            }),
        ),
    }),
]);

export type ScheduleSourcePreviousCoding = z.infer<
    typeof scheduleSourcePreviousCodingSchema
>;

export const scheduleApplicationBaseSchema = z.object({
    id: z.string().uuid(),
    scheduleTemplateId: z.string().uuid(),
    workspaceId: z.string().uuid(),
    entityId: z.number().int(),
    type: scheduleTypeSchema,
    sourceType: scheduleSourceTypeSchema,
    transactionId: z.number().int().nullable(),
    invoiceId: z.string().uuid().nullable(),
    billId: z.string().uuid().nullable(),
    manualSourceLabel: z.string().nullable(),
    sourceLineIds: z.array(z.string().uuid()).nullable(),
    ledgerId: z.string().uuid(),
    amount: z.number(),
    termStart: z.string(),
    specificDate: z.string().nullable(),
    counterpartyId: z.string().uuid().nullable(),
    status: scheduleApplicationStatusSchema,
    sourcePreviousCoding: scheduleSourcePreviousCodingSchema.nullable(),
    termLength: z.number().int().positive().nullable(),
    termUnit: scheduleTermUnitSchema.nullable(),
    createdByUserId: z.number().nullable(),
    createdAt: z.coerce.date(),
    updatedByUserId: z.number().nullable(),
    updatedAt: z.coerce.date(),
});

export type ScheduleApplicationBase = z.infer<
    typeof scheduleApplicationBaseSchema
>;

export const scheduleApplicationPeriodRoleSchema = z.nativeEnum(
    ScheduleApplicationPeriodRole,
);

export const scheduleApplicationPeriodBaseSchema = z.object({
    id: z.string().uuid(),
    scheduleApplicationId: z.string().uuid(),
    periodKey: z.string(),
    effectiveDate: z.string(),
    amount: z.number(),
    componentOrder: z.number().int(),
    subtype: scheduleJeSubtypeSchema,
    status: scheduleApplicationPeriodStatusSchema,
    role: scheduleApplicationPeriodRoleSchema,
    /** Ladder generation the row's journal entry carries. Apply writes 0. */
    revision: z.number().int(),
    lifecycleVerb: scheduleLifecycleVerbSchema.nullable(),
    /** Reversal rows only: the row whose journal entry this one cancels. */
    reversesPeriodId: z.string().uuid().nullable(),
    /** Filled when the row's journal entry posts; null while pending or voided. */
    debitAccountId: z.string().uuid().nullable(),
    creditAccountId: z.string().uuid().nullable(),
    description: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type ScheduleApplicationPeriodBase = z.infer<
    typeof scheduleApplicationPeriodBaseSchema
>;

export const allocationPercentSchema = z
    .number()
    .gt(0)
    .max(100)
    .multipleOf(ALLOCATION_PERCENT_STEP);

export const scheduleMilestoneAnchorSchema = z.nativeEnum(
    ScheduleMilestoneAnchor,
);
export const scheduleMilestoneOffsetUnitSchema = z.nativeEnum(
    ScheduleMilestoneOffsetUnit,
);

export const scheduleTemplateMilestoneBaseSchema = z.object({
    id: z.string().uuid(),
    scheduleTemplateId: z.string().uuid(),
    sortOrder: z.number().int().positive(),
    name: z.string(),
    allocationPercent: z.number(),
    anchor: scheduleMilestoneAnchorSchema,
    offsetCount: z.number().int().nullable(),
    offsetUnit: scheduleMilestoneOffsetUnitSchema.nullable(),
    specificDate: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type ScheduleTemplateMilestoneBase = z.infer<
    typeof scheduleTemplateMilestoneBaseSchema
>;

/**
 * Milestone detail for one application period. The period carries the row's
 * status, amount and journal entry link; this row carries the plan snapshot.
 */
export const scheduleApplicationMilestoneBaseSchema = z.object({
    id: z.string().uuid(),
    scheduleApplicationId: z.string().uuid(),
    scheduleApplicationPeriodId: z.string().uuid(),
    sortOrder: z.number().int().positive(),
    name: z.string(),
    allocationPercent: z.number(),
    plannedAmount: z.number(),
    expectedDate: z.string(),
    postingDate: z.string().nullable(),
    isOverRecognized: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type ScheduleApplicationMilestoneBase = z.infer<
    typeof scheduleApplicationMilestoneBaseSchema
>;
